import { omit, isEmpty, toLower } from 'lodash';
import { axios } from './common';
import { CM_BASE_URL } from '../../pages/constants/cmConstants';
import { convertFilterEpochForBackend } from '../../util/timezone';

export const fetchAlerts = async ({ filters, currentPage, pageSize }) => {
  const params = {
    level: filters.alertLevel,
    typologyIds: !isEmpty(filters.typologyIds)
      ? filters.typologyIds.join(',')
      : null,
    sources: !isEmpty(filters.sourceIds) ? filters.sourceIds.join(',') : null,
    ...omit(filters, [
      'alertLevel',
      'searchField',
      'searchText',
      'typologyIds',
      'sourceIds'
    ]),
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    from: convertFilterEpochForBackend(filters.from),
    to: convertFilterEpochForBackend(filters.to)
  };

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/alerts`;

  return axios.get(url, { params }).then((res) => {
    if (res.status !== 200) {
      throw new Error('Failed to fetch alerts');
    }
    return res.data;
  });
};

export const fetchAlertDetailsById = async (alertId) => {
  const url = `${CM_BASE_URL}/alerts/byId/${alertId}`;
  const res = await axios.get(url);

  if (res.status !== 200 && res.status !== 404) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const fetchWhitelistByCustomer = async (alertId) => {
  const url = `${CM_BASE_URL}/alerts/${alertId}/whitelists`;
  const res = await axios.get(url);

  if (res.status !== 200 && res.status !== 404) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const updateWhitelistByCustomer = async (payload) => {
  const { startDate, endDate, notes, alertId } = payload;

  const requestBody = {
    startDate,
    endDate,
    notes
  };
  const url = `${CM_BASE_URL}/alerts/${alertId}/whitelists`;
  const res = await axios.put(url, requestBody);

  if (res.status !== 200 && res.status !== 404 && res.status !== 202) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const changeAlertStatus = async ({ alertId, status }) => {
  const url = `${CM_BASE_URL}/alerts/${alertId}/status/${status}`;

  return axios.put(url).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw new Error(res.data?.msg ?? 'Failed to change alert status');
    }
    return res.data;
  });
};

export const assignAlertsToUser = async ({ alertIds, userId }) => {
  const requestBody = {
    alertIds,
    user: userId
  };

  const url = `${CM_BASE_URL}/alerts/assignToUser`;

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw new Error(
        res.data?.msg ??
          'Something went wrong. Please try again after sometime.'
      );
    }
    return res.data;
  });
};

export const addAlertNote = async ({ alertId, alertNote }) => {
  const requestBody = {
    alertId,
    note: alertNote
  };

  const url = `${CM_BASE_URL}/history/addNote`;

  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: 'Failed to add alert note' };
    }
    return res.data;
  });
};

export const createCaseFromAlerts = async ({ alertIds, departmentId }) => {
  const requestBody = {
    alertIds,
    departmentId
  };
  const url = `${CM_BASE_URL}/cases/fromAlerts`;
  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw new Error(res.data.error ?? 'Failed to convert alert(s) to a case');
    }
    return res.data;
  });
};

export const mergeAlertsToCase = async ({ alertIds, caseId }) => {
  const requestBody = {
    alertIds,
    caseId
  };
  const url = `${CM_BASE_URL}/cases/${caseId}/addAlerts`;
  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: res.data.error };
    }
    return res.data;
  });
};

// FIN-10455: Suppress raw BE errors (which leak Scala Option values and
// internal IDs) and show one of two PO-approved messages:
//   - 401/403 or body code AUTH_ATTRIBUTEERROR → authorization message
//   - anything else → generic hard-failure message
// FIN-10510 (re-open 2026-04-23): Also match the BE's AttributeAuth response
// body code, since HTTP status can be rewritten by proxies/gateways and the
// body is the authoritative signal that BE rejected on authorization grounds.
// FIN-10455 (re-open 2026-04-27): For the RE_OPENED transition only, mirror
// the alert-details page and surface the BE's per-user `msg` (e.g.
// "User <email> is not authorized to reopen the alert"). All other
// transitions still use the static AUTH_MSG so we don't leak Scala-Option
// debug strings. The auth-detection logic lives in both the try (axios is
// configured with validateStatus:false so non-2xx lands here) and the catch
// (interceptor- or network-rejected paths).
export const bulkUpdateAlertStatus = async ({ alertIds, status, reason }) => {
  const url = `${CM_BASE_URL}/alerts/bulkUpdateStatus`;
  const AUTH_MSG =
    'You are not authorized to bulk update alerts assigned to other users.';
  const GENERIC_MSG =
    'Bulk update could not be completed. Please try again or contact your system administrator.';
  const isReopen = status === 'RE_OPENED';
  const pickAuthMsg = (data) => (isReopen && data?.msg ? data.msg : AUTH_MSG);

  try {
    const res = await axios.post(url, { alertIds, status, reason });
    if (res.status === 200) return res.data;
    const code = res.data?.code;
    const isAuth =
      res.status === 401 ||
      res.status === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    throw new Error(isAuth ? pickAuthMsg(res.data) : GENERIC_MSG);
  } catch (error) {
    if (error.message === AUTH_MSG || error.message === GENERIC_MSG) {
      throw error;
    }
    const httpStatus = error.response?.status;
    const responseData = error.response?.data;
    const code = responseData?.code;
    const isAuth =
      httpStatus === 401 ||
      httpStatus === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    if (isAuth) throw new Error(pickAuthMsg(responseData));
    // Re-throw any custom auth msg already produced (e.g. BE detail msg from try)
    if (error.message && error.message !== '') throw error;
    throw new Error(GENERIC_MSG);
  }
};

// FIN-10888 / FIN-10527: Suppress raw BE errors (which leak Scala Option values
// and case IDs) and show one of two PO-approved messages:
//   - 401/403 or body code AUTH_ATTRIBUTEERROR → authorization message
//   - anything else → generic hard-failure message
// FIN-10515: Accept doChecker for checker review workflow
// FIN-10510 (re-open 2026-04-23): Also match the BE's AttributeAuth response
// body code, since HTTP status can be rewritten by proxies/gateways and the
// body is the authoritative signal that BE rejected on authorization grounds.
// FIN-10527 / FIN-10888 (re-open 2026-04-27): Reverted FIN-10905's detailed
// `responseData.msg` passthrough — it was leaking the BE's Scala-Option
// debug strings (e.g. "Case: 23292895 User: None") into the UI. PO-approved
// copy is the single static AUTH_MSG below regardless of which auth subrule
// the BE flagged.
export const bulkUpdateCaseStatus = async ({
  caseIds,
  status,
  reason,
  doChecker
}) => {
  const url = `${CM_BASE_URL}/cases/bulkUpdateStatus`;
  const AUTH_MSG =
    'You are not authorized to bulk update cases assigned to other users.';
  const GENERIC_MSG =
    'Bulk update could not be completed. Please try again or contact your system administrator.';

  // FIN-10905 (re-open 2026-04-29): BE has cleaned up the Scala-Option
  // debug strings — `msg` is now a user-friendly sentence explaining the
  // specific reason (e.g. cross-dept, must self-assign). Surface it directly
  // so the user understands why the action was rejected. Falls back to
  // AUTH_MSG if `msg` is missing or empty.
  const pickAuthMsg = (data) => {
    const msg = data?.msg;
    return typeof msg === 'string' && msg.trim() !== '' ? msg : AUTH_MSG;
  };

  try {
    const res = await axios.post(url, { caseIds, status, reason, doChecker });
    if (res.status === 200) return res.data;
    const code = res.data?.code;
    const isAuth =
      res.status === 401 ||
      res.status === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    throw new Error(isAuth ? pickAuthMsg(res.data) : GENERIC_MSG);
  } catch (error) {
    if (error.message === GENERIC_MSG) {
      throw error;
    }
    const httpStatus = error.response?.status;
    const responseData = error.response?.data;
    const code = responseData?.code;
    if (
      httpStatus === 401 ||
      httpStatus === 403 ||
      code === 'AUTH_ATTRIBUTEERROR'
    ) {
      throw new Error(pickAuthMsg(responseData));
    }
    // Re-throw any custom auth msg already produced (e.g. BE detail msg from try)
    if (error.message) throw error;
    throw new Error(GENERIC_MSG);
  }
};

// FIN-10098: Apply alert status to other alerts in case
export const applyAlertStatusBulkInCase = async ({
  caseId,
  alertIds,
  targetStatus,
  reason,
  triggeringAlertId
}) => {
  const url = `${CM_BASE_URL}/alerts/bulk-status-in-case`;

  return axios
    .put(url, { caseId, alertIds, targetStatus, reason, triggeringAlertId })
    .then((res) => {
      if (res.status !== 200) {
        throw new Error('Failed to apply alert status to case alerts');
      }
      return res.data;
    });
};

export const fetchAlertsRelayHistoryList = async ({
  filters = [],
  currentPage,
  pageSize
}) => {
  const params = {
    status: filters?.status,
    isLatest: filters?.isLatest,
    offset: (currentPage - 1) * pageSize,
    limit: pageSize
  };

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/retry-history/all`;

  return axios.get(url, { params }).then((res) => {
    if (res.status !== 200) {
      throw new Error('Failed to fetch alert retry history');
    }
    return res.data;
  });
};
